import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Container, SectionHeading } from '../components/ui';
import PageHero from '../components/PageHero';
import FAQAccordion from '../components/FAQAccordion';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import { faqJsonLd } from '../data/seo';
import { FAQ_CATEGORIES } from '../data/faqPage';

export default function Faq() {
  const location = useLocation();
  const [active, setActive] = useState('all');
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  // Deep-link support: /faq#payments filters to that category and scrolls to it.
  useEffect(() => {
    const id = (location.hash || '').replace('#', '');
    if (id && FAQ_CATEGORIES.some((c) => c.id === id)) {
      setActive(id);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }, [location.hash]);

  // Filter by search text (across question + answer) and by active category.
  const results = useMemo(() => {
    return FAQ_CATEGORIES
      .filter((c) => active === 'all' || c.id === active)
      .map((c) => ({
        ...c,
        items: q ? c.items.filter((it) => (it.q + ' ' + it.a).toLowerCase().includes(q)) : c.items,
      }))
      .filter((c) => c.items.length > 0);
  }, [active, q]);

  const totalMatches = results.reduce((n, c) => n + c.items.length, 0);

  return (
    <div>
      <Seo
        title="Frequently Asked Questions"
        description="Answers about BillPortal — utility bill management, payments, managed services, partners, Astrid & AI, KPIs, support, vendor management and onboarding."
        path="/faq"
        jsonLd={faqJsonLd(FAQ_CATEGORIES.flatMap((c) => c.items))}
      />

      <PageHero
        eyebrow="Help Centre"
        title="Frequently asked questions"
        subtitle="Everything you need to know about BillPortal — from how utility bill management and pricing work to payments, partners, Astrid & AI, and getting onboarded."
      />

      {/* Search + category filter bubbles */}
      <section className="px-5 pt-8 sm:px-8" data-testid="faq-controls">
        <Container>
          <div className="mx-auto max-w-2xl">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions…"
                data-testid="faq-search"
                aria-label="Search FAQs"
                className="h-12 w-full rounded-full border border-slate-200 bg-white pl-12 pr-11 text-sm text-slate-900 outline-none transition-colors focus:border-brand dark:border-white/10 dark:bg-white/[0.03] dark:text-white"
              />
              {query && (
                <button onClick={() => setQuery('')} data-testid="faq-search-clear" aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-2.5" data-testid="faq-filters">
            {(() => {
              const isActive = active === 'all';
              return (
                <button
                  onClick={() => setActive('all')}
                  data-testid="faq-filter-all"
                  aria-pressed={isActive}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? 'border border-brand bg-brand text-white'
                      : 'border border-slate-200 bg-white text-slate-700 hover:border-brand hover:text-brand dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-200'
                  }`}
                >
                  All
                </button>
              );
            })()}
            {FAQ_CATEGORIES.map((c) => {
              const I = c.icon;
              const isActive = active === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  data-testid={`faq-filter-${c.id}`}
                  aria-pressed={isActive}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? 'border border-brand bg-brand text-white'
                      : 'border border-slate-200 bg-white text-slate-700 hover:border-brand hover:text-brand dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-200'
                  }`}
                >
                  <I className={`h-4 w-4 ${isActive ? 'text-white' : 'text-brand'}`} /> {c.label}
                </button>
              );
            })}
          </div>

          {q && (
            <p className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400" data-testid="faq-result-count">
              {totalMatches} result{totalMatches === 1 ? '' : 's'} for “{query.trim()}”
            </p>
          )}
        </Container>
      </section>

      {/* Results */}
      {results.length === 0 ? (
        <section className="px-5 py-20 sm:px-8">
          <Container>
            <div className="mx-auto max-w-md rounded-2xl border border-dashed border-slate-200 p-10 text-center dark:border-white/10" data-testid="faq-empty">
              <p className="text-base font-semibold text-slate-800 dark:text-white">No matching questions</p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Try a different search, or reset the filters.</p>
              <button onClick={() => { setQuery(''); setActive('all'); }} className="mt-4 inline-flex items-center rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:opacity-90">Reset</button>
            </div>
          </Container>
        </section>
      ) : (
        results.map((c, idx) => {
          const I = c.icon;
          return (
            <section
              key={c.id}
              id={c.id}
              data-testid={`faq-section-${c.id}`}
              className={`scroll-mt-24 px-5 py-14 sm:px-8 ${idx % 2 === 1 ? 'bg-slate-50 dark:bg-white/[0.02]' : ''}`}
            >
              <Container>
                <div className="mb-8 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand"><I className="h-5 w-5" /></span>
                  <div>
                    <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white md:text-xl">{c.label}</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{c.blurb}</p>
                  </div>
                </div>
                <FAQAccordion key={`${c.id}-${q}`} items={c.items} className="mx-0 max-w-none" />
              </Container>
            </section>
          );
        })
      )}

      <section className="px-5 py-16 sm:px-8">
        <Container>
          <SectionHeading
            eyebrow="Still have questions?"
            title="We’re happy to help"
            subtitle="Can’t find what you’re looking for? Reach out and our team will get back to you."
          />
        </Container>
      </section>
    </div>
  );
}
